/**
 * Window bridge for the asset library.
 *
 * Exposes `window.__cio_assets` so code outside React (Parcel editors,
 * Ember components, the embed framecast bridge) can open the asset
 * picker and await a selection.
 *
 * The bridge writes directly to the `$assetLibraryModal` nanostore
 * atom — no React context needed.
 */

import { type Asset } from '@/services/db/asset/entity';
import { uploadAsset } from '@/services/db/asset/upload-asset';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { $assetLibraryModal } from './browser/store';
import { type AssetLibraryOptions } from './browser/types';

export interface CioAssetsBridge {
  /** Open the asset picker and resolve with the selected asset, or null if closed. */
  selectAsset: (options?: AssetLibraryOptions) => Promise<Asset | null>;
  /** Upload a file and return the created asset. */
  uploadFile: (file: File, parentFolderId?: string | null) => Promise<Asset>;
  /** Whether the bridge is ready (AssetLibrary component is mounted). */
  isReady: boolean;
}

declare global {
  interface Window {
    __cio_assets?: CioAssetsBridge;
  }
}

/**
 * Install the bridge on `window.__cio_assets` and fire a ready event.
 * Call this once when `<AssetLibrary />` mounts.
 */
export function installAssetBridge(): void {
  window.__cio_assets = {
    selectAsset: (options?: AssetLibraryOptions): Promise<Asset | null> => {
      return new Promise((resolve) => {
        $assetLibraryModal.set({
          isOpen: true,
          callback: resolve,
          options: options ?? null,
        });
      });
    },
    uploadFile: (file: File, parentFolderId?: string | null): Promise<Asset> => {
      const workspaceId = requireWorkspaceId();
      return uploadAsset({ workspaceId, file, parentFolderId });
    },
    isReady: true,
  };
  window.dispatchEvent(new Event('cio-assets-ready'));
}

/**
 * Remove the bridge from `window.__cio_assets`.
 * Call this when `<AssetLibrary />` unmounts.
 */
export function uninstallAssetBridge(): void {
  if (window.__cio_assets != null) {
    window.__cio_assets.isReady = false;
  }
  delete window.__cio_assets;
}
